import CryptoJS from 'crypto-js';

/**
 * Servicio de encriptación/desencriptación
 * Implementa el patrón Singleton para ser accesible globalmente
 */
export class CryptoService {
  private static instance: CryptoService;
  private readonly encryptionKey: CryptoJS.lib.WordArray;
  
  private constructor() {
    // Obtener clave de encriptación desde variables de entorno
    const keyHex = process.env.REACT_APP_ENCRYPTION_KEY || '';
    if (!keyHex) {
      throw new Error('La clave de encriptación no está configurada en las variables de entorno');
    }
    this.encryptionKey = CryptoJS.enc.Hex.parse(keyHex);
  }
  
  /**
   * Obtiene la instancia única del servicio de encriptación
   */
  public static getInstance(): CryptoService {
    if (!CryptoService.instance) {
      CryptoService.instance = new CryptoService();
    }
    return CryptoService.instance;
  }
  
  /**
   * Encripta datos utilizando AES
   * @param data - Datos a encriptar
   * @returns Datos encriptados en forma de cadena
   */
  public encrypt(data: any): string {
    if (!data) return '';
    
    // Convertir a JSON string si es un objeto
    const dataStr = typeof data === 'object' ? JSON.stringify(data) : String(data);
    
    // Encriptar usando AES en modo ECB
    const encrypted = CryptoJS.AES.encrypt(dataStr, this.encryptionKey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Convertir a formato Hex
    return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
  }
  
  /**
   * Desencripta datos utilizando AES
   * @param encryptedData - Datos encriptados
   * @returns Datos desencriptados (objeto o cadena)
   */
  public decrypt(encryptedData: any): any {
    if (!encryptedData) return null;
    
    try {
      // Si no es string, intentar convertirlo
      const encryptedHex = typeof encryptedData === 'string' 
        ? encryptedData 
        : (encryptedData.encryptedData || JSON.stringify(encryptedData));
      
      // Crear un objeto CipherParams con el contenido hexadecimal
      const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Hex.parse(encryptedHex)
      });
      
      // Desencriptar usando AES en modo ECB
      const decrypted = CryptoJS.AES.decrypt(
        cipherParams, 
        this.encryptionKey, 
        {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }
      );
      
      // Convertir a UTF-8
      const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
      
      // Intentar parsear como JSON
      try {
        return JSON.parse(decryptedStr);
      } catch (e) {
        // Si no es JSON, devolver como string
        return decryptedStr;
      }
    } catch (error) {
      console.error('Error al desencriptar datos:', error);
      return encryptedData;
    }
  }
} 